/**
 * PipingTrackerDropdown - Dropdown for switching between piping sub-views
 * 
 * Used within DisciplineMatrix to allow users to switch between:
 * - Progress Tracker (standard piping tracking)
 * - NDE Tracker (non-destructive examination tracking)
 * - Testing Tracker (ASME B31.3/B31.1 pressure testing)
 * - Welder Log (welder database management)
 */

import React, { useState, useRef, useEffect } from 'react';

export const PIPING_SUB_VIEWS = [
  {
    id: 'progress-tracker',
    label: 'Progress Tracker',
    disciplineId: 'piping',
  },
  {
    id: 'nde-tracker',
    label: 'NDE Tracker',
    disciplineId: 'piping-nde',
  },
  {
    id: 'testing-tracker',
    label: 'Testing Tracker',
    disciplineId: 'piping-testing',
  },
];

export const getPipingSubViewById = (subViewId) => {
  return PIPING_SUB_VIEWS.find(v => v.id === subViewId) || PIPING_SUB_VIEWS[0];
};

const PipingTrackerDropdown = ({ currentSubViewId, onSubViewChange, disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const currentView = getPipingSubViewById(currentSubViewId);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleSelect = (subViewId) => {
    if (subViewId !== currentSubViewId) {
      onSubViewChange(subViewId);
    }
    setIsOpen(false);
  };

  const dropdownStyle = {
    position: 'relative',
    display: 'inline-block',
  };

  const buttonStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    backgroundColor: disabled ? '#e9ecef' : '#fff',
    border: '1px solid #ced4da',
    borderRadius: '6px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    color: disabled ? '#6c757d' : '#212529',
    minWidth: '180px',
    justifyContent: 'space-between',
    transition: 'all 0.2s ease',
  };

  const menuStyle = {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: '4px',
    backgroundColor: '#fff',
    border: '1px solid #ced4da',
    borderRadius: '6px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    zIndex: 1000,
    overflow: 'hidden',
  };

  const menuItemStyle = (isSelected) => ({
    padding: '10px 12px',
    cursor: 'pointer',
    backgroundColor: isSelected ? '#e3f2fd' : 'transparent',
    borderLeft: isSelected ? '3px solid #1976d2' : '3px solid transparent',
    fontSize: '14px',
    color: isSelected ? '#1565c0' : '#212529',
    fontWeight: isSelected ? '500' : '400',
    transition: 'all 0.15s ease',
  });

  const labelStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    color: '#6c757d',
    marginRight: '12px',
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <div style={labelStyle}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
          <path d="M4 6h16v2H4zm0 5h16v2H4zm0 5h16v2H4z"/>
        </svg>
        <span>View:</span>
      </div>

      <div ref={dropdownRef} style={dropdownStyle}>
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          style={buttonStyle}
          disabled={disabled}
          onMouseEnter={(e) => {
            if (!disabled) {
              e.currentTarget.style.borderColor = '#80bdff';
              e.currentTarget.style.boxShadow = '0 0 0 2px rgba(0, 123, 255, 0.1)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#ced4da';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <span>{currentView.label}</span>
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="currentColor"
            style={{
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
            }}
          >
            <path d="M7 10l5 5 5-5z" />
          </svg>
        </button>

        {isOpen && (
          <div style={menuStyle}>
            {PIPING_SUB_VIEWS.map((view) => (
              <div
                key={view.id}
                onClick={() => handleSelect(view.id)}
                style={menuItemStyle(view.id === currentSubViewId)}
                onMouseEnter={(e) => {
                  if (view.id !== currentSubViewId) {
                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                  }
                }}
                onMouseLeave={(e) => {
                  if (view.id !== currentSubViewId) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                {view.label}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PipingTrackerDropdown;
